import { BankDetail, BankDetailStatus } from '@services/appsync/types';
import segment from '@services/segment';
import { fetchAuthSession } from 'aws-amplify/auth';
import session from './session';

type BankProps = {
  type: BankDetail['account_type'];
  name: BankDetail['name'];
  is_default: BankDetail['default'];
  status: BankDetail['status'] | null;
};
type PricingViewedProps = {
  experiment: 'A' | 'B';
};

type Action =
  | (BankProps & { action: 'Bank Account Default Set' })
  | (PricingViewedProps & { action: 'Pricing Viewed' })
  | { action: 'Application Permission Requested' }
  | { action: 'Button Clicked'; name: string }
  | {
      action: 'Bank Account Verification Started';
      name: string;
      is_default: boolean;
      status: BankDetailStatus;
      type: string | null | undefined;
    }
  | {
      action: 'Bank Account Verification Completed';
      name: string;
      is_default: boolean;
      status: BankDetailStatus;
      type: string | null | undefined;
    }
  | {
      action: 'Bank Account Verification Failed';
      name: string;
      is_default: boolean;
      status: BankDetailStatus;
      type: string | null | undefined;
    }
  | { action: 'Chat Conversation Ended'; name: string }
  | { action: 'Chat Conversation Started'; name: string }
  | { action: 'Chat Message Received'; name: string }
  | { action: 'Chat Message Sent'; name: string }
  | { action: 'Data Viz Interacted'; name: string }
  | { action: 'Data Viz Viewed'; name: string }
  | { action: 'Deep Link'; target: 'inbound' | 'outbound'; link: string }
  | { action: 'Disclosure Agreed'; name: string }
  | { action: 'Disclosure Dismissed'; name: string }
  | { action: 'Disclosure Viewed'; name: string }
  | { action: 'Document Downloaded'; name: string }
  | { action: 'Document Shared'; name: string }
  | { action: 'Document Viewed'; name: string }
  | { action: 'Form Started'; name: string }
  | { action: 'Form Submitted'; name: string; fields: Record<string, number | string> }
  | { action: 'Image Viewed'; name: string }
  | { action: 'Modal Viewed'; name: string }
  | { action: 'Notification Dismissed'; name: string }
  | { action: 'Notification Received'; name: string }
  | { action: 'Notification Tapped'; name: string }
  | { action: 'Push Notification Dismissed'; name: string }
  | { action: 'Push Notification Received'; name: string }
  | { action: 'Push Notification Tapped'; name: string }
  | { action: 'Screen Viewed'; name: string }
  | { action: 'Text Read'; name: string }
  | { action: 'User Confirm Identity Completed' }
  | { action: 'User Confirm Identity Started' }
  | { action: 'User Creation Completed' }
  | { action: 'User Creation Started' }
  | { action: 'User Password Reset Completed' }
  | { action: 'User Password Reset Failed' }
  | { action: 'User Password Reset Requested' }
  | { action: 'User Phone Verified' }
  | { action: 'User Sign In Failed' }
  | { action: 'User Signed In' }
  | { action: 'User Signed Out' }
  | { action: 'User Updated' }
  | { action: 'Video Completed'; name: string }
  | { action: 'Video Progressed'; name: string }
  | { action: 'Video Started'; name: string }
  | { action: 'Waitlist Joined'; email: string }
  | {
      action: 'Web Component Viewed';
      view:
        | 'SignupEmailForm'
        | 'SignupPartnerWelcome'
        | 'SignupPasswordForm'
        | 'SignupPhoneForm'
        | 'SignupNameForm'
        | 'SignupPhoneVerificationCodeForm'
        | 'SignupSuccessMessage';
    };

function identify({ userId, traits = undefined }: { userId?: string; traits?: Record<string, unknown> }) {
  segment.identify(userId, traits as any);
}

function page() {
  segment.page();
}

async function start() {
  try {
    if (!session.get()) return;
    const amplifySession = await fetchAuthSession();
    const user = amplifySession.tokens?.idToken?.payload;

    if (user) {
      identify({
        traits: {
          email: user?.email || null,
          firstName: user?.given_name || null,
          lastName: user?.family_name || null,
          name: `${user?.name || null}`,
        },
        userId: user.sub,
      });
    }
  } catch (err: any) {
    // noop
  }
}

function reset() {
  segment.reset();
}

function track({ action, ...props }: Action) {
  segment.track(action, props as unknown);
}

let hasInitialized = false;
export default (() => {
  if (!hasInitialized) {
    hasInitialized = true;
  }
  (window as any).concreit = (window as any).concreit || {};
  (window as any).concreit.tracking = { track };
  return {
    identify,
    page,
    reset,
    start,
    track,
  };
})();
